import { ServicePage } from '@/components/ui/page-templates/ServicePage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, Scale, Lightbulb, Handshake } from 'lucide-react';

export function AdvisoryPage() {
  const breadcrumbs = [
    { label: 'Services', href: '/services' },
    { label: 'Advisory', href: '/services/advisory', current: true },
  ];

  return (
    <ServicePage
      variant="partner"
      title="Advisory Services"
      description="Strategic and operational advisory for leadership teams—from governance and M&A to growth and transformation."
      breadcrumbs={breadcrumbs}
      category="Professional Services"
    >
      <div className="space-y-14">
        <section className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Our Advisory Offerings</h2>
            <p className="mt-2 max-w-3xl text-muted-foreground">
              Independent, senior-level advisory to boards, executives, and investors
              on strategy, governance, transactions, and organizational transformation.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card variant="bordered" className="border-primary/20 transition-shadow hover:shadow-md">
              <CardHeader>
                <Briefcase className="h-8 w-8 mb-4 text-primary" />
                <CardTitle>Strategy & Governance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Advisory on corporate strategy, board effectiveness, and governance best practices.
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Strategic planning and review</li>
                  <li>Board and committee advisory</li>
                  <li>Risk and compliance frameworks</li>
                  <li>Executive and leadership development</li>
                </ul>
              </CardContent>
            </Card>

            <Card variant="bordered" className="border-primary/20 transition-shadow hover:shadow-md">
              <CardHeader>
                <Scale className="h-8 w-8 mb-4 text-primary" />
                <CardTitle>M&A & Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Support through mergers, acquisitions, divestitures, and capital events.
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Due diligence and valuation</li>
                  <li>Deal structuring and negotiation</li>
                  <li>Integration and carve-out planning</li>
                  <li>Post-merger optimization</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold tracking-tight">How We Engage</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Insight & Analysis</h3>
                <p className="text-muted-foreground">
                  We bring independent perspective and deep expertise to your most important decisions.
                </p>
              </CardContent>
            </Card>
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Handshake className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Partnership</h3>
                <p className="text-muted-foreground">
                  We work alongside leadership as trusted advisors, not just consultants.
                </p>
              </CardContent>
            </Card>
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Execution Support</h3>
                <p className="text-muted-foreground">
                  We help turn recommendations into action with clear ownership and milestones.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </ServicePage>
  );
}
